import { isEmpty } from "lodash";
import { api } from "services";

import {
	STORAGE_AWARD_ACTIVE_COLS,
	STORAGE_PROPOSAL_ACTIVE_COLS,
	STORAGE_MODIFICATIONS_ACTIVE_COLS,
	AWARDS_LIST_DEFAULT_COLUMNS,
	PROPOSAL_MAIN_VIEW_LIST_DEFAULT_COLUMNS,
	DEFAULT_MODIFICATION_COLUMN_HEADERS,
	STORAGE_SUBAWARDS_MODIFICATIONS_ACTIVE_COLS,
	DEFAULT_SUBAWARD_MODIFICATION_COLUMN_HEADERS,
	SUBAWARDS_LIST_DEFAULT_COLUMNS,
	STORAGE_SUBAWARD_ACTIVE_COLS,
} from "globals/constants";
import { TABLE_TYPES } from "globals/constants/tableTypes";

import { dispatch } from "../store";
import { notifyError } from "../utils";

const storageKeys = {
	AWARD: STORAGE_AWARD_ACTIVE_COLS,
	SUBAWARD: STORAGE_SUBAWARD_ACTIVE_COLS,
	PROPOSAL: STORAGE_PROPOSAL_ACTIVE_COLS,
	AWARD_MODIFICATION: STORAGE_MODIFICATIONS_ACTIVE_COLS,
	SUBAWARD_MODIFICATION: STORAGE_SUBAWARDS_MODIFICATIONS_ACTIVE_COLS,
};

const defaultColumnsMap = {
	AWARD: AWARDS_LIST_DEFAULT_COLUMNS,
	SUBAWARD: SUBAWARDS_LIST_DEFAULT_COLUMNS,
	PROPOSAL: PROPOSAL_MAIN_VIEW_LIST_DEFAULT_COLUMNS,
	AWARD_MODIFICATION: DEFAULT_MODIFICATION_COLUMN_HEADERS,
	SUBAWARD_MODIFICATION: DEFAULT_SUBAWARD_MODIFICATION_COLUMN_HEADERS,
};

const actionTypeMap = {
	AWARD: "AWARDS_SET_ACTIVE_COLS",
	SUBAWARD: "SUBAWARDS_SET_ACTIVE_COLS",
	PROPOSAL: "PROPOSALS_SET_ACTIVE_COLS",
	AWARD_MODIFICATION: "MODIFICATIONS_SET_ACTIVE_COLS",
	SUBAWARD_MODIFICATION: "SUBAWARDS_MODIFICATIONS_SET_ACTIVE_COLS",
};

const getStorageKey = type => storageKeys[type];
const getDefaultColumns = type => defaultColumnsMap[type] || [];

const ensureAlwaysActiveColumns = (type, defaultColumns, currentActiveColumns) => {
	if (TABLE_TYPES.SUBAWARD_MODIFICATION !== type) {
		return currentActiveColumns;
	}
	const alwaysActiveColumns = defaultColumns.filter(column => column.alwaysActive);
	const activeColumnIds = new Set(currentActiveColumns.map(column => column.id));
	const missingAlwaysActiveColumns = alwaysActiveColumns.filter(column => !activeColumnIds.has(column.id));

	return missingAlwaysActiveColumns.length > 0
		? [ ...missingAlwaysActiveColumns, ...currentActiveColumns ]
		: currentActiveColumns;
};

export const loadActiveColumns = (type, formColumnHeaders = null) => async () => {
	const storageKey = getStorageKey(type);
	let defaultColumns = getDefaultColumns(type);
	const actionType = actionTypeMap[type];

	try {
		const data = await api.userColumnsPreferences.get(TABLE_TYPES[type]);
		let activeColumns = data.activeColumns ? data.activeColumns.filter(Boolean) : defaultColumns;
		activeColumns = ensureAlwaysActiveColumns(type, defaultColumns, activeColumns);
		if (formColumnHeaders && ![ TABLE_TYPES.AWARD_MODIFICATION, TABLE_TYPES.SUBAWARD_MODIFICATION ].includes(type)) {
			activeColumns = activeColumns.filter(col =>
				formColumnHeaders.some(header => header.id === col.id)
			);
		}

		dispatch({ type: actionType, columns: activeColumns });
		sessionStorage.setItem(storageKey, JSON.stringify(activeColumns));
	} catch (error) {
		if (error.response?.status === 404) {
			if ("AWARD_MODIFICATION" === type && formColumnHeaders) {
				defaultColumns = formColumnHeaders;
				await updateColumns(defaultColumns, "AWARD_MODIFICATION");
			}
			dispatch({ type: actionType, columns: defaultColumns });
			sessionStorage.setItem(storageKey, JSON.stringify(defaultColumns));

			return;
		}
		notifyError("Error loading columns", error);
	}
};

export const updateColumns = async (columns, type, formColumnHeaders = null, saveToDatabase = true) => {
	if (!columns || columns.length === 0 || type === undefined) {
		return;
	}

	let validColumns = columns;
	if (formColumnHeaders) {
		validColumns = columns.filter(col =>
			formColumnHeaders.some(header => header.id === col.id)
		);

		if (validColumns.length === 0) {
			return;
		}
	}

	const currentColumns = await getCurrentActiveColumns(TABLE_TYPES[type]);

	if (areArraysEqual(currentColumns, validColumns)) {
		return currentColumns;
	}

	const requestBody = {
		tableType: TABLE_TYPES[type],
		activeColumns: validColumns,
	};

	const actionType = actionTypeMap[type];

	try {
		if (saveToDatabase || (!saveToDatabase && isEmpty(currentColumns))) {
			await api.userColumnsPreferences.save(requestBody);
			dispatch({ type: actionType, columns: validColumns });
			sessionStorage.setItem(getStorageKey(type), JSON.stringify(validColumns));
		}

		return validColumns;
	} catch (error) {
		notifyError("Error updating columns", error);
	}
};

const getCurrentActiveColumns = async tableType => {
	try {
		const data = await api.userColumnsPreferences.get(tableType);

		return data.activeColumns || [];
	} catch (error) {
		return [];
	}
};

const areColumnsEqual = (col1, col2) => col1.id === col2.id && col1.columnHeader === col2.columnHeader;

const areArraysEqual = (arr1, arr2) => {
	const filteredArr2 = arr2.filter(col => col !== undefined);
	if (arr1.length !== filteredArr2.length) {
		return false;
	}

	const map = new Map(filteredArr2.map(col => [ col.id, col ]));

	return arr1.every(col1 => {
		const col2 = col1 ?  map.get(col1.id) : null;

		return col2 && areColumnsEqual(col1, col2);
	});
};